import React, { useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import { getProvinceName } from '../../utils/provinceNames';

/**
 * Domanda «Territorio di competenza»: territorio del rispondente bloccato
 * (deciso dal server) e una riga per provincia con le opzioni come radio.
 * Da `md` in su tabella (prima colonna fissa), sotto schede impilate.
 */

export interface TerritorioRispondente {
  livello: string;
  regioni: string[];
  province: string[];
}

export interface TerritorioQuestionData {
  questionId: string;
  options: { optionId: string; text: string; value: string }[];
}

interface TerritorioQuestionProps {
  question: TerritorioQuestionData;
  territorio: TerritorioRispondente;
  value: Record<string, string>;
  onChange: (provincia: string, value: string) => void;
  disabled?: boolean;
}

/** Territorio di esempio per l'anteprima (sito ed editor): Abruzzo. */
export const TERRITORIO_ESEMPIO = (livello = 'regionale'): TerritorioRispondente => ({
  livello,
  regioni: ['Abruzzo'],
  province: ['AQ', 'CH', 'PE', 'TE'],
});

const MEDIA_MD = '(min-width: 768px)';

const leggiMedia = (): boolean => {
  try {
    return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(MEDIA_MD).matches;
  } catch {
    return false;
  }
};

// Una sola resa alla volta: due gruppi di radio con lo stesso `name` nel DOM
// si toglierebbero la spunta a vicenda.
function useSchermoMedio(): boolean {
  const [medio, setMedio] = useState<boolean>(leggiMedia);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(MEDIA_MD);
    const aggiorna = () => setMedio(mq.matches);
    aggiorna();
    if (mq.addEventListener) {
      mq.addEventListener('change', aggiorna);
      return () => mq.removeEventListener('change', aggiorna);
    }
    mq.addListener(aggiorna);
    return () => mq.removeListener(aggiorna);
  }, []);
  return medio;
}

const TerritorioQuestion: React.FC<TerritorioQuestionProps> = ({ question, territorio, value, onChange, disabled = false }) => {
  const schermoMedio = useSchermoMedio();
  const regionale = territorio.livello === 'regionale';
  const elenco = regionale ? territorio.regioni : territorio.province;
  const options = question.options || [];
  const nomeGruppo = (provincia: string) => `${question.questionId}-${provincia}`;
  const compilate = territorio.province.filter(p => !!value[p]).length;

  const intestazione = (
    <div className="mb-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/40 px-3 py-2">
      <p className="flex items-center gap-1.5 text-sm text-gray-800 dark:text-gray-200">
        <Lock className="w-3.5 h-3.5 flex-shrink-0 text-gray-500 dark:text-gray-400" aria-hidden="true" />
        <span>
          <span className="font-medium">{regionale ? 'Regione di competenza' : 'Provincia di competenza'}:</span>{' '}
          {elenco.join(', ')}
        </span>
      </p>
      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
        Se il territorio non è corretto, contatta la Segreteria
      </p>
    </div>
  );

  if (territorio.province.length === 0 || options.length === 0) {
    return (
      <div>
        {intestazione}
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {options.length === 0 ? 'Nessuna opzione di risposta configurata.' : 'Nessuna provincia nel territorio di competenza.'}
        </p>
      </div>
    );
  }

  const conteggio = (
    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400" aria-live="polite">
      Province compilate: {compilate} di {territorio.province.length}
    </p>
  );

  if (schermoMedio) {
    return (
      <div>
        {intestazione}
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-600">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/60">
                <th scope="col" className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-700 text-left px-3 py-2 text-xs font-medium text-gray-600 dark:text-gray-300 min-w-[160px]">
                  Provincia
                </th>
                {options.map(opt => (
                  <th key={opt.optionId} scope="col" className="px-2 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300 min-w-[72px]">
                    {opt.text}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {territorio.province.map(provincia => {
                const nome = getProvinceName(provincia);
                return (
                  <tr key={provincia} className="bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700">
                    {/* sfondo opaco: la colonna fissa copre le altre nello scorrimento orizzontale */}
                    <th scope="row" className="sticky left-0 z-10 bg-white dark:bg-gray-800 text-left px-3 py-2 font-normal">
                      <span className="font-semibold text-gray-900 dark:text-white">{provincia}</span>{' '}
                      <span className="text-xs text-gray-500 dark:text-gray-400">{nome}</span>
                    </th>
                    {options.map(opt => (
                      <td key={opt.optionId} className="px-2 py-1 text-center">
                        <label className={`inline-flex items-center justify-center w-full py-1.5 rounded ${disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-blue-50 dark:hover:bg-gray-700'}`}>
                          <input
                            type="radio"
                            name={nomeGruppo(provincia)}
                            value={opt.value}
                            checked={value[provincia] === opt.value}
                            onChange={() => onChange(provincia, opt.value)}
                            disabled={disabled}
                            className="w-4 h-4 text-blue-600 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-500"
                          />
                          <span className="sr-only">{`${provincia} ${nome}: ${opt.text}`}</span>
                        </label>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {conteggio}
      </div>
    );
  }

  return (
    <div>
      {intestazione}
      <div className="space-y-3">
        {territorio.province.map(provincia => {
          const nome = getProvinceName(provincia);
          return (
            <fieldset key={provincia} className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-3">
              <legend className="px-1 text-sm">
                <span className="font-semibold text-gray-900 dark:text-white">{provincia}</span>{' '}
                <span className="text-xs text-gray-500 dark:text-gray-400">{nome}</span>
              </legend>
              <div className="space-y-1.5">
                {options.map(opt => (
                  <label
                    key={opt.optionId}
                    className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${value[provincia] === opt.value ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400' : 'border-gray-200 dark:border-gray-600'} ${disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                  >
                    <input
                      type="radio"
                      name={nomeGruppo(provincia)}
                      value={opt.value}
                      checked={value[provincia] === opt.value}
                      onChange={() => onChange(provincia, opt.value)}
                      disabled={disabled}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-500"
                    />
                    <span className="text-gray-700 dark:text-gray-300">{opt.text}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          );
        })}
      </div>
      {conteggio}
    </div>
  );
};

export default TerritorioQuestion;
